/**
 * Provider text that arrives as markup, as something a record can show.
 *
 * External records render as plain text -- an email body's `>` quotes and
 * asterisks are not markdown -- so the markup has to go somewhere before the
 * preview pane sees it.
 */

/**
 * HTML as readable text.
 *
 * DOMParser rather than a regex: entities and nested markup are exactly the
 * things a regex gets wrong, and a parsed document is never inserted into the
 * page, so nothing in it runs.
 */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  // Block-level tags carry the line breaks that textContent would otherwise
  // collapse, so mark them before flattening.
  doc
    .querySelectorAll("br, p, div, tr, li, h1, h2, h3, h4, h5, h6")
    .forEach((el) => el.append("\n"));
  doc.querySelectorAll("script, style").forEach((el) => el.remove());
  return (doc.body.textContent ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * The same, but only for text that actually carries markup.
 *
 * Fields that are *sometimes* HTML -- a calendar event's description is the
 * one here -- must not be run through the parser unconditionally: a plain-text
 * description that happens to contain `&` or `<` would come back altered for
 * no reason. A tag-ish opening is the cheap test for which kind this is.
 */
export function maybeHtmlToText(text: string): string {
  return /<[a-z!/]/i.test(text) ? htmlToText(text) : text.trim();
}
